import posthog, { type CaptureResult } from "posthog-js";

import { FOREIGN_MESSAGE, OWN_SCRIPT, SENTRY_CHUNK } from "@/lib/error-filters";
import type { User } from "@/types/user";

let enabled = false;

interface ExceptionEntry {
  value?: string;
  stacktrace?: { frames?: { filename?: string; abs_path?: string }[] };
}

// Drops errors not thrown by this site's own code (see error-filters.ts).
function dropForeignErrors(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== "$exception") return event;
  const exceptions = (event.properties.$exception_list ?? []) as ExceptionEntry[];
  const foreign = exceptions.some((e) => {
    const urls = (e.stacktrace?.frames ?? [])
      .map((frame) => frame.filename || frame.abs_path || "")
      // Built-ins (JSON.parse, Array.reduce) report "<anonymous>" or
      // "[native code]"; the script that called them is the next frame down.
      .filter((url) => url !== "" && url !== "<anonymous>" && url !== "[native code]");
    // Frames run oldest first, so the last one is where the error was thrown.
    const url = urls[urls.length - 1] ?? "";
    return (
      FOREIGN_MESSAGE.test(e.value ?? "") ||
      (url !== "" && (!OWN_SCRIPT.test(url) || SENTRY_CHUNK.test(url)))
    );
  });
  return foreign ? null : event;
}

/** Until this runs, the helpers below are no-ops, so callers never need to check. */
export function initPostHog(): void {
  posthog.init("phc_LBWGGE289NOf8baQoCIYP9l38yODewgjI2rZnkAfYYi", {
    api_host: "https://eu.i.posthog.com",
    // 'history_change' pageviews for the SPA router; the cookie is shared
    // across *.appio.so, so www/docs visitors carry over to sign-up.
    defaults: "2026-08-30",
    person_profiles: "identified_only",
    capture_exceptions: true,
    before_send: dropForeignErrors,
  });
  enabled = true;
}

/** Safe to call repeatedly. */
export function identifyUser(user: User): void {
  if (!enabled) return;
  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
    organization_id: user.organization_id,
  });
}

/** Starts a fresh anonymous identity, so the next sign-in on this browser is not merged into this user. */
export function resetAnalytics(): void {
  if (!enabled) return;
  posthog.reset();
}

export function captureException(error: unknown, properties?: Record<string, unknown>): void {
  if (!enabled) return;
  posthog.captureException(error, properties);
}

export function captureEvent(name: string, properties?: Record<string, unknown>): void {
  if (!enabled) return;
  posthog.capture(name, properties);
}
